import "./StackedBarChart.css";

import type { Scale, ScriptableContext, TooltipItem } from "chart.js";
import { ChartData } from "chart.js";
import { Bar } from "react-chartjs-2";

import { ColorConstants } from "../../../constants";
import { useChartVariant } from "../ChartVariantContext";
import { ChartVariant, getChartTheme, maybeSetGlobalChartJsConfiguration, themeFont } from "../utils";

maybeSetGlobalChartJsConfiguration();

export interface StackedChartData {
  label: string;
  data: number[];
  /**
   * One colour for the whole series, or one colour per category when the series is coloured by bar
   * rather than by stack position - the Nutrient Neutrality inventory chart, where each nutrient
   * carries its own colour family.
   */
  backgroundColor: string | string[];
  hoverBackgroundColor: string | string[];
}

export interface StackedBarChartProps {
  xLabels: (string | string[])[];
  data: StackedChartData[];
  emptyPlaceholder?: string;
  hasYAxisGridLines?: boolean;
  hasYAxisTicks?: boolean;
  showLegend?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  yAxisTickCallbackFunction?: (this: Scale, val: string | number, index: number) => any;
  customTooltipLabel?: (context: TooltipItem<"bar">) => string;
  /** Unit label rendered beside the y axis, e.g. "kg/yr". Omitted, the axis has no title. */
  yAxisTitle?: string;
  /**
   * Design-system brand: `"hub"` (Space Grotesk, square) or `"seed"` (Figtree, rounded).
   * Defaults to the nearest ChartVariantProvider (Hub if none).
   */
  variant?: ChartVariant;
}

export const StackedBarChart = ({
  xLabels,
  data,
  emptyPlaceholder = "No data to display",
  hasYAxisTicks = false,
  hasYAxisGridLines = false,
  showLegend = true,
  yAxisTickCallbackFunction,
  customTooltipLabel,
  yAxisTitle,
  variant,
}: StackedBarChartProps): JSX.Element => {
  const contextVariant = useChartVariant();
  const theme = getChartTheme(variant ?? contextVariant);
  const options = {
    plugins: {
      title: {
        display: false,
      },
      legend: {
        display: showLegend,
        reverse: true,
        position: "bottom" as const,
        labels: {
          padding: 40,
          boxWidth: 16,
          boxHeight: 16,
          font: themeFont(theme),
          // Seed uses rounded (point-style) keys; Hub keeps square boxes.
          usePointStyle: theme.legendUsePointStyle,
          pointStyle: theme.legendPointStyle,
        },
      },
      tooltip: {
        displayColors: false,
        cornerRadius: theme.tooltipCornerRadius,
        titleFont: themeFont(theme),
        bodyFont: themeFont(theme),
        callbacks: {
          title: () => "",
          label:
            customTooltipLabel || ((context: TooltipItem<"bar">) => `${context.dataset.label}: ${context.parsed.y}`),
        },
      },
    },
    animation: {
      duration: 0,
    },
    devicePixelRatio: 4, // Use 4 for clarity
    responsive: true,
    scales: {
      x: {
        beginAtZero: true,
        stacked: true,
        ticks: {
          padding: 6,
          font: themeFont(theme),
        },
        grid: {
          display: false,
          drawTicks: false,
        },
        border: {
          color: ColorConstants.GREY_100,
        },
      },
      y: {
        beginAtZero: true,
        stacked: true,
        // Mirrors LineChart's y-axis title. Undefined reproduces the previous options object exactly.
        title: yAxisTitle
          ? {
              display: true,
              text: yAxisTitle,
              padding: 12,
              font: {
                size: 14,
                weight: 500,
                family: theme.fontFamily,
              },
            }
          : undefined,
        ticks: {
          callback: yAxisTickCallbackFunction,
          display: hasYAxisTicks,
          padding: 24,
          font: themeFont(theme),
        },
        grid: {
          display: hasYAxisGridLines,
          color: ColorConstants.INDIGO_25,
          tickColor: ColorConstants.WHITE,
          drawTicks: false,
        },
        border: {
          display: false,
        },
      },
    },
  };

  if (!data || Object.keys(data).length === 0)
    return (
      <div className="StackedBar StackedBarEmpty">
        <p className="body3">{emptyPlaceholder}</p>
      </div>
    );

  // The visible top of a stack is the last *non-zero* dataset for that category — not
  // necessarily the last dataset (e.g. inventory charts where trailing series are zero
  // for a given vintage). Round that segment so the Seed cap follows the real top.
  const topVisibleDatasetIndex = (dataIndex: number): number => {
    for (let d = data.length - 1; d >= 0; d -= 1) {
      if ((data[d].data[dataIndex] ?? 0) > 0) return d;
    }
    return -1;
  };
  const isTopVisibleSegment = (ctx: ScriptableContext<"bar">): boolean =>
    ctx.datasetIndex === topVisibleDatasetIndex(ctx.dataIndex);

  const chartData: ChartData<"bar", number[], string | string[]> = {
    labels: xLabels,
    datasets: data.map((chart) => ({
      label: chart.label,
      data: chart.data,
      backgroundColor: chart.backgroundColor,
      hoverBackgroundColor: chart.hoverBackgroundColor,
      // Match the single BarChart's slim, gapped proportions.
      barPercentage: 0.62,
      categoryPercentage: 0.7,
      maxBarThickness: 56,
      // Round only the visible top of each stack (scriptable, per category), giving the same
      // rounded cap as the single BarChart while inner segments stay flush. Hub = square.
      borderRadius: (ctx: ScriptableContext<"bar">) => (isTopVisibleSegment(ctx) ? theme.barBorderRadius : 0),
      borderSkipped: (ctx: ScriptableContext<"bar">) => (isTopVisibleSegment(ctx) ? "bottom" : false),
    })),
  };

  return (
    <div className="StackedBar">
      <Bar options={options} data={chartData} />
    </div>
  );
};
